import { IWidget } from 'common';

export type QuickLayout = 'carousel' | 'wall' | 'mosaic';

export const layoutSettings: Record<QuickLayout, Partial<IWidget['settings']>> = {
  carousel: {
    type: 'carousel',
  },
  wall: {
    type: 'wall',
  },
  mosaic: {
    type: 'mosaic',
  },
};

/**
 * Determine which visual layout is active based on current settings
 */
export const matchLayout = (settings: IWidget['settings'], layout: QuickLayout): boolean => {
  switch (layout) {
    case 'carousel':
      return settings.type === 'carousel';
    case 'wall':
      return settings.type === 'wall';
    case 'mosaic':
      return settings.type === 'mosaic';
    default:
      return false;
  }
};

/**
 * Gets the current layout based on widget settings
 */
export const getCurrentLayout = (settings: IWidget['settings']): QuickLayout => {
  for (const layout of ['carousel', 'wall', 'mosaic'] as QuickLayout[]) {
    if (matchLayout(settings, layout)) {
      return layout;
    }
  }
  // Default to wall if no match is found so we always fall back to a grid layout
  return 'wall';
};

/**
 * Updates quick settings based on individual property changes from advanced settings
 */
export const updateSettingsFromAdvanced = (
  propertyName: string,
  propertyValue: any,
): Partial<IWidget['settings']> => {
  // copy of the basic change
  const newSettings: Partial<IWidget['settings']> = {
    [propertyName]: propertyValue,
  };

  // Special handling for combined settings
  if (propertyName === 'type' && propertyValue === 'carousel') {
    // When changing to carousel in advanced settings, preserve the current shop_this_look_display
    // This way we don't reset the user's preferred shop this look behavior
    // No need to add anything since we don't want to change shop_this_look_display
  } else if (propertyName === 'shop_this_look_display') {
    // When changing shop_this_look_display, ensure shop_this_look is updated accordingly
    newSettings.shop_this_look = propertyValue !== 'none';
  }

  return newSettings;
};

/**
 * Check if the current layout supports content size adjustments
 */
export const supportsContentSize = (settings: IWidget['settings']): boolean => {
  const { type } = settings;
  return type === 'wall' || type === 'carousel';
};
